import { DeleteOutlined } from '@ant-design/icons';
import {
  Button,
  Checkbox,
  Empty,
  Form,
  InputNumber,
  Select,
  Typography,
} from 'antd';
import { useEffect, useMemo, useRef, useState } from 'react';

import { useLazyGetWarehouseProductsForSaleQuery } from '@app/api/Products';
import type { ModelProductForSale } from '@app/api/Products/types';
import type {
  ProductSaleDraftItem,
  ProductSaleItemsType,
} from '@app/components/RecordModal/components/RecordDetails/types';
import { NumberInputWithMask } from '@app/components/RecordModal/components/RecordDetails/ui/NumberInputWithMask';
import { WithTooltip } from '@app/components/WithTooltip';
import { FRONT_WARNING, WARNING } from '@app/constants';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';
import { createId, divideAmount, withTestId } from '@app/utils';

import type { DefaultOptionType } from 'antd/es/select';

import '@app/components/RecordModal/components/RecordDetails/styles/ProductSaleItems.desktop.scss';

const { Item } = Form;
const { Text } = Typography;
const PF = 'product-sale-items';

const createProductSaleDraftItem = (): ProductSaleDraftItem => ({
  id: createId(),
  discountCurrency: 0,
  discountPercent: 0,
  sellerId: null,
  groupId: null,
  discounts: [],
  totalDiscount: 0,
  paid: 0,
  cost: 0,
  isExcludedFromLoyaltyDiscount: false,
});

const getCountAvailable = (product: ModelProductForSale): number => {
  if (typeof product.CountAvailable === 'number') return product.CountAvailable;
  if (typeof product.CountAvaialbe === 'number') return product.CountAvaialbe;
  return 0;
};

const getProductSaleTotal = ({
  quantity = 0,
  price = 0,
  discountCurrency = 0,
}: ProductSaleDraftItem) => {
  const total = quantity * price - discountCurrency;

  return total > 0 ? +total.toFixed(2) : 0;
};

export const ProductSaleItems: ProductSaleItemsType = ({
  warehouses,
  branchUserOptions,
  activeBranchId,
  items = [],
  onChange,
  disabled,
}) => {
  const { showNotification } = useNotification();
  const [getWarehouseProductsForSale] =
    useLazyGetWarehouseProductsForSaleQuery();
  const [productsByItemId, setProductsByItemId] = useState<
    Record<string, ModelProductForSale[]>
  >({});
  const [loadingProductItemIds, setLoadingProductItemIds] = useState<
    Set<string>
  >(new Set());
  const loadedWarehousesRef = useRef<Map<string, number>>(new Map());

  const itemsRef = useRef(items);
  itemsRef.current = items;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const itemsKey = useMemo(
    () => items.map((item) => `${item.id}:${item.warehouseId ?? ''}`).join('|'),
    [items]
  );

  const warehouseOptions: DefaultOptionType[] = useMemo(
    () =>
      warehouses.map(({ Id, Name }) => ({
        value: Id,
        label: Name || `Склад ${Id}`,
      })),
    [warehouses]
  );

  const totalAmount = useMemo(
    () => items.reduce((acc, item) => acc + getProductSaleTotal(item), 0),
    [items]
  );

  const totalQuantity = useMemo(
    () => items.reduce((acc, item) => acc + (item.quantity || 0), 0),
    [items]
  );

  const updateItem = (
    itemId: string,
    handler: (item: ProductSaleDraftItem) => ProductSaleDraftItem
  ) => {
    onChangeRef.current(
      itemsRef.current.map((item) =>
        item.id === itemId ? handler(item) : item
      )
    );
  };

  const setItemProductsLoading = (itemId: string, isLoading: boolean) => {
    setLoadingProductItemIds((currentIds) => {
      const nextIds = new Set(currentIds);

      if (isLoading) {
        nextIds.add(itemId);
      } else {
        nextIds.delete(itemId);
      }

      return nextIds;
    });
  };

  const loadWarehouseProducts = async (itemId: string, warehouseId: number) => {
    if (activeBranchId === null) {
      showNotification('Активный филиал не найден', FRONT_WARNING);
      return;
    }

    setItemProductsLoading(itemId, true);

    try {
      const products = await getWarehouseProductsForSale({
        ActiveBranchId: activeBranchId,
        warehouseId,
      }).unwrap();

      setProductsByItemId((currentProducts) => ({
        ...currentProducts,
        [itemId]: products,
      }));
      loadedWarehousesRef.current.set(itemId, warehouseId);
    } catch (error) {
      setProductsByItemId((currentProducts) => ({
        ...currentProducts,
        [itemId]: [],
      }));
      showNotification(error, WARNING);
    } finally {
      setItemProductsLoading(itemId, false);
    }
  };

  const loadWarehouseProductsRef = useRef(loadWarehouseProducts);
  loadWarehouseProductsRef.current = loadWarehouseProducts;

  useEffect(() => {
    const snapshot = itemsRef.current;

    snapshot.forEach((item) => {
      if (!item.warehouseId) return;
      const loaded = loadedWarehousesRef.current.get(item.id);
      if (loaded === item.warehouseId) return;
      loadWarehouseProductsRef.current(item.id, item.warehouseId);
    });

    const activeIds = new Set(snapshot.map((item) => item.id));

    loadedWarehousesRef.current.forEach((_, id) => {
      if (!activeIds.has(id)) loadedWarehousesRef.current.delete(id);
    });

    setProductsByItemId((current) => {
      let hasStaleEntries = false;
      const next: Record<string, ModelProductForSale[]> = {};
      Object.entries(current).forEach(([id, value]) => {
        if (activeIds.has(id)) {
          next[id] = value;
        } else {
          hasStaleEntries = true;
        }
      });
      return hasStaleEntries ? next : current;
    });
  }, [itemsKey]);

  useEffect(() => {
    const enriched = itemsRef.current.map((item) => {
      if (!item.productId || item.countAvailable !== undefined) return item;
      const product = productsByItemId[item.id]?.find(
        (candidate) => candidate.ProductId === item.productId
      );
      if (!product) return item;
      const nextPrice =
        item.price === undefined ? product.PriceOfSale : item.price;
      const nextCount = getCountAvailable(product);
      return {
        ...item,
        productName: item.productName || product.ProductName,
        price: nextPrice,
        countAvailable: nextCount,
      };
    });

    const hasChanges = enriched.some(
      (item, index) => item !== itemsRef.current[index]
    );

    if (hasChanges) onChangeRef.current(enriched);
  }, [productsByItemId]);

  const addItemHandler = () => {
    onChange([...items, createProductSaleDraftItem()]);
  };

  const deleteItemHandler = (itemId: string) => {
    onChange(items.filter((item) => item.id !== itemId));
    setProductsByItemId((currentProducts) => {
      const nextProducts = { ...currentProducts };
      delete nextProducts[itemId];

      return nextProducts;
    });
    loadedWarehousesRef.current.delete(itemId);
    setItemProductsLoading(itemId, false);
  };

  const warehouseChangeHandler = (itemId: string, warehouseId: number) => {
    updateItem(itemId, (item) => ({
      ...item,
      warehouseId,
      productId: undefined,
      productName: undefined,
      quantity: undefined,
      price: undefined,
      countAvailable: undefined,
      discountCurrency: 0,
      discountPercent: 0,
    }));
    setProductsByItemId((currentProducts) => ({
      ...currentProducts,
      [itemId]: [],
    }));
    loadedWarehousesRef.current.delete(itemId);
  };

  const productChangeHandler = (itemId: string, productId: number) => {
    const selectedProduct = productsByItemId[itemId]?.find(
      (product) => product.ProductId === productId
    );

    if (!selectedProduct) return;

    updateItem(itemId, (item) => ({
      ...item,
      productId,
      productName: selectedProduct.ProductName,
      quantity: undefined,
      price: selectedProduct.PriceOfSale,
      countAvailable: getCountAvailable(selectedProduct),
      discountCurrency: 0,
      discountPercent: 0,
    }));
  };

  const quantityChangeHandler = (itemId: string, value?: number) => {
    updateItem(itemId, (item) => {
      const total = (item.price || 0) * (value || 0);
      const nextDiscountPercent =
        total > 0 ? ((item.discountCurrency || 0) / total) * 100 : 0;

      return {
        ...item,
        quantity: value,
        discountPercent: nextDiscountPercent,
      };
    });
  };

  const isExcludedFromLoyaltyDiscountChangeHandler = (
    itemId: string,
    value: boolean
  ) => {
    updateItem(itemId, (item) => {
      return {
        ...item,
        isExcludedFromLoyaltyDiscount: value,
      };
    });
  };

  const discountCurrencyChangeHandler = (itemId: string, value: number) => {
    updateItem(itemId, (item) => {
      const total = (item.price || 0) * (item.quantity || 0);
      const discountCurrency = Math.min(value, total);
      const discountPercent = total > 0 ? (discountCurrency / total) * 100 : 0;

      return {
        ...item,
        discountCurrency,
        discountPercent,
      };
    });
  };

  const discountPercentChangeHandler = (itemId: string, value: number) => {
    updateItem(itemId, (item) => {
      const discountPercent = Math.min(value, 100);
      const total = (item.price || 0) * (item.quantity || 0);
      const discountCurrency =
        total > 0 ? Number(((discountPercent / 100) * total).toFixed(2)) : 0;

      return {
        ...item,
        discountCurrency,
        discountPercent,
      };
    });
  };

  const sellerChangeHandler = (itemId: string, sellerId?: number) => {
    updateItem(itemId, (item) => ({
      ...item,
      sellerId: sellerId ?? null,
    }));
  };

  const getProductOptions = (
    item: ProductSaleDraftItem
  ): DefaultOptionType[] => {
    return (productsByItemId[item.id] || [])
      .filter(
        (product) =>
          getCountAvailable(product) > 0 || product.ProductId === item.productId
      )
      .map((product) => {
        const { ProductId, ProductName, PriceOfSale } = product;
        const countAvailable = getCountAvailable(product);

        return {
          value: ProductId,
          name: ProductName || '',
          label: (
            <div className={`${PF}__product-label`}>
              <Text strong>{ProductName || 'Товар без названия'}</Text>
              <span>
                {divideAmount(PriceOfSale)} / доступно {countAvailable} шт
              </span>
            </div>
          ),
        };
      });
  };

  const getSelectedProductName = (item: ProductSaleDraftItem) => {
    const productNameFromOptions = productsByItemId[item.id]?.find(
      (product) => product.ProductId === item.productId
    )?.ProductName;

    return productNameFromOptions || item.productName || 'Товар без названия';
  };

  const getQuantityHelp = (item: ProductSaleDraftItem) => {
    if (!item.productId) return undefined;

    if (!item.quantity) return 'Введите количество';

    if (!Number.isInteger(item.quantity)) return 'Введите целое число';

    if (item.quantity < 1) return 'Не может быть меньше 1';

    if (
      item.countAvailable !== undefined &&
      item.quantity > item.countAvailable
    ) {
      return `Не может быть больше ${item.countAvailable}`;
    }

    return undefined;
  };

  const getDiscountHelp = (item: ProductSaleDraftItem) => {
    if (!item.productId) return undefined;

    if (item.discountCurrency < 0) return 'Не может быть меньше 0';

    const total = (item.price || 0) * (item.quantity || 0);

    if (item.discountCurrency > total) return 'Не может быть больше цены';

    if (item.discountPercent < 0) return 'Не может быть меньше 0';

    if (item.discountPercent > 100) return 'Не может быть больше 100%';

    return undefined;
  };

  return (
    <div className={PF}>
      <div className={`${PF}__notice`}></div>
      {items.length === 0 && (
        <Button
          type="primary"
          onClick={addItemHandler}
          disabled={disabled || warehouses.length === 0}
          data-testid="add-product-sale-item"
          style={{
            width: '210px',
          }}
        >
          Добавить товар
        </Button>
      )}
      {items.length > 0 && (
        <>
          <div className={`${PF}__scroll-wrapper`}>
            <div className={`${PF}__items`}>
              <div className={`${PF}__header`}>
                <div />
                <Text strong>Склад</Text>
                <Text strong>Товар</Text>
                <Text strong>Кол-во</Text>
                <Text strong>Цена</Text>
                <Text strong>Скидка</Text>
                <Text strong>Продавец</Text>
                <Text strong className={`${PF}__header-total`}>
                  К оплате
                </Text>
                <div />
              </div>
              {items.map((item) => {
                const productOptions = getProductOptions(item);
                const isProductLoading = loadingProductItemIds.has(item.id);
                const productDisabled =
                  disabled || !item.warehouseId || isProductLoading;
                const quantityHelp = getQuantityHelp(item);
                const discountHelp = getDiscountHelp(item);
                const rowTotal = getProductSaleTotal(item);

                return (
                  <div className={`${PF}__item`} key={item.id}>
                    <WithTooltip
                      show={true}
                      title={
                        item.productId
                          ? 'Исключить из расчёта системы лояльности'
                          : 'Сначала выберите товар'
                      }
                    >
                      <Item label="" className={`${PF}__checkbox`}>
                        <Checkbox
                          disabled={disabled || !item.productId}
                          checked={item.isExcludedFromLoyaltyDiscount}
                          onChange={(e) =>
                            isExcludedFromLoyaltyDiscountChangeHandler(
                              item.id,
                              e.target.checked
                            )
                          }
                        />
                      </Item>
                    </WithTooltip>
                    <Item label="Склад" className={`${PF}__field`}>
                      <Select
                        showSearch
                        optionFilterProp="label"
                        placeholder="Выберите склад"
                        options={warehouseOptions}
                        value={item.warehouseId}
                        disabled={disabled || warehouses.length === 0}
                        data-testid="product-sale-warehouse"
                        onChange={(value) =>
                          warehouseChangeHandler(item.id, value)
                        }
                        notFoundContent={
                          <Empty
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                            description="Нет складов"
                          />
                        }
                      />
                    </Item>
                    <WithTooltip
                      show={!item.warehouseId}
                      title="Сначала выберите склад"
                    >
                      <Item label="Товар" className={`${PF}__field`}>
                        <Select
                          showSearch
                          optionFilterProp="name"
                          placeholder="Выберите товар"
                          options={productOptions}
                          value={item.productId}
                          disabled={productDisabled}
                          loading={isProductLoading}
                          data-testid="product-sale-product"
                          onChange={(value) =>
                            productChangeHandler(item.id, value)
                          }
                          labelRender={() => (
                            <span>{getSelectedProductName(item)}</span>
                          )}
                          notFoundContent={
                            <Empty
                              image={Empty.PRESENTED_IMAGE_SIMPLE}
                              description={
                                item.warehouseId
                                  ? 'Нет товаров для продажи'
                                  : 'Выберите склад'
                              }
                            />
                          }
                        />
                      </Item>
                    </WithTooltip>
                    <WithTooltip
                      show={!item.productId}
                      title="Сначала выберите товар"
                    >
                      <Item label="Кол-во" className={`${PF}__field`}>
                        <InputNumber
                          min={0}
                          step={1}
                          precision={0}
                          value={item.quantity}
                          disabled={disabled || !item.productId}
                          status={quantityHelp ? 'error' : undefined}
                          data-testid="product-sale-quantity"
                          onChange={(value) =>
                            quantityChangeHandler(
                              item.id,
                              value === null ? undefined : value
                            )
                          }
                        />
                        {quantityHelp && (
                          <div className={`${PF}__error`}>{quantityHelp}</div>
                        )}
                      </Item>
                    </WithTooltip>
                    <Item label="Цена" className={`${PF}__field`}>
                      <NumberInputWithMask
                        value={item.price || 0}
                        disabled
                        dataTestId="product-sale-price"
                      />
                    </Item>
                    <WithTooltip
                      show={!item.productId}
                      title="Сначала выберите товар"
                    >
                      <Item
                        label="Скидка"
                        className={`${PF}__field ${PF}__field-discount`}
                      >
                        <NumberInputWithMask
                          value={item.discountCurrency}
                          disabled={disabled || !item.productId}
                          dataTestId="product-sale-discount-price"
                          onChange={(value) =>
                            discountCurrencyChangeHandler(item.id, value)
                          }
                        />
                        <NumberInputWithMask
                          value={item.discountPercent}
                          disabled={disabled || !item.productId}
                          dataTestId="product-sale-discount-percent"
                          suffix="%"
                          onChange={(value) =>
                            discountPercentChangeHandler(item.id, value)
                          }
                        />
                        {discountHelp && (
                          <div className={`${PF}__error`}>{discountHelp}</div>
                        )}
                      </Item>
                    </WithTooltip>
                    <WithTooltip
                      show={!item.productId}
                      title="Сначала выберите товар"
                    >
                      <Item label="Продавец" className={`${PF}__field`}>
                        <Select
                          options={branchUserOptions}
                          value={item.sellerId}
                          disabled={disabled || !item.productId}
                          showSearch
                          allowClear
                          optionFilterProp="label"
                          data-testid="product-sale-seller"
                          onChange={(value) =>
                            sellerChangeHandler(item.id, value)
                          }
                          popupRender={withTestId(
                            'product-sale-seller-dropdown'
                          )}
                        />
                      </Item>
                    </WithTooltip>
                    <div className={`${PF}__total`}>
                      <Text strong className={`${PF}__total-label`}>
                        К оплате
                      </Text>
                      <Text data-testid="product-sale-total">
                        {divideAmount(rowTotal)}
                      </Text>
                    </div>
                    <Button
                      icon={<DeleteOutlined />}
                      onClick={() => deleteItemHandler(item.id)}
                      disabled={disabled}
                      data-testid="delete-product-sale-item"
                    />
                  </div>
                );
              })}
              <div className={`${PF}__item ${PF}__item-summary`}>
                <div />
                <div className={`${PF}__field`}>
                  <Text strong>Итого:</Text>
                </div>
                <div />
                <div className={`${PF}__field`}>
                  <Text className="total-quantity">
                    {divideAmount(totalQuantity)} шт
                  </Text>
                </div>
                <div />
                <div />
                <div />
                <div className={`${PF}__total`}>
                  <Text>{divideAmount(totalAmount)}</Text>
                </div>
                <div />
              </div>
            </div>
          </div>
          <div className={`${PF}__footer`}>
            <Button
              type="primary"
              onClick={addItemHandler}
              disabled={disabled || warehouses.length === 0}
              data-testid="add-product-sale-item"
            >
              Добавить товар
            </Button>
          </div>
        </>
      )}
    </div>
  );
};
